/**
 * Domains store module — `/my/domains` on the Domain object (design v1.5.1
 * §15, Phase 6.5).
 *
 * Holds the org-scoped list of Domains (zone, datacenter, public provider,
 * private lane, delegation, edge state, verification), a per-domain records
 * cache with the public-lane hint the server returns for self-managed
 * domains, a per-domain expected-records cache (provider `none`), the
 * datacenter options, and the last claim result (the verification token +
 * TXT record shown in the verify step of `AddDomainModal`).
 *
 * Namespaced, NOT persisted — domain data is always re-fetched.
 *
 * Typed errors from the service layer are kept apart so the UI renders
 * specific copy:
 *   - `DomainNotVerified` → `recordsErrorByDomain[d] = {kind:'not_verified'}`
 *     (inline banner, not a toast)
 *   - `PublicLaneSelfManaged` → a warn toast + the expected records land in
 *     `expectedByDomain[d]` so the Records tab can show them at once
 *   - `DomainConflict` / `DomainPolicyError` → rendered inline by the wizard
 *   - `DomainAccessDenied` (no `domain:viewer` — asked of auth-api before the
 *     list, or a 403 `forbidden` from it) → `domainsAccessDenied = true`, an
 *     empty list, no throw: a Member without a grant is not an error
 *   - `DomainLimitReached` (409 `domain_limit_reached`, shared tier) → the
 *     allowance is marked full and the wizard renders the plan sentence
 *
 * Scope (2026-10-23, personal domains): `domainsScope` is `organization`
 * (Team and above — unchanged) or `personal` (a Free/Starter customer in a
 * shared system org: their own domain, `domainLimit` of them). The decision
 * is `loadDomainsScoped` in `utils/personal-domain.js`.
 *   - everything else → a domain-specific error string + a toast
 *
 * Per-row "is running" state lives in `busy`, keyed by
 * `${scope}:${domain}:${action}`.
 */
import {
  domainsApi,
  DomainNotVerified,
  DomainConflict,
  DomainPolicyError,
  DomainAccessDenied,
  DomainLimitReached,
  PublicLaneSelfManaged
} from '@/services/domains-api'
import { isSuperAdminUser } from '@/utils/environments-visibility'
import { DOMAIN_SCOPE, domainAllowance, isSharedTierUser, isSystemOrgUser, loadDomainsScoped } from '@/utils/personal-domain'

const state = () => ({
  // ── Domains list ─────────────────────────────────────────────────────────
  domains: [],
  domainsLoading: false,
  domainsError: null,
  // True when the permission plane says this caller may not list domains in
  // the current org (a Member without a `domain` grant). Not an error: the
  // page shows who can grant access and nothing is requested from dns-manager.
  domainsAccessDenied: false,
  // `organization` (today) or `personal` (shared tier: the caller's own domain).
  domainsScope: DOMAIN_SCOPE.ORGANIZATION,
  // The scope the server actually answered (null = it said nothing — an older dns-manager).
  domainsScopeAnswered: null,
  // false when dns-manager could not confirm the plan (`meta.decided: false`).
  domainsScopeDecided: true,
  // Personal allowance from the server (`meta` / `/domains/access`); null = unlimited / unknown.
  domainLimit: null,
  domainsUsed: null,
  // The server's reason when a personal caller may not manage domains (plain text only).
  domainAccessReason: null,
  // /me says the active org is a system org — only words the old-backend refusal.
  domainsSystemOrg: false,
  // The plan hint the last 409 domain_limit_reached carried.
  domainPlanHint: null,
  // True once the canonical route answered; false means the alias served us
  // (older dns-manager → placement fields are absent).
  canonical: true,

  // ── Per-domain records cache ────────────────────────────────────────────
  recordsByDomain: {}, // { 'example.com': [ ...records ] }
  privateRecordsByDomain: {}, // { 'example.com': [ ...private-only ] }
  publicLaneByDomain: {}, // { 'example.com': { provider, self_managed, expected_records_url } }
  recordsLoadingByDomain: {}, // { 'example.com': true }
  recordsErrorByDomain: {}, // { 'example.com': { kind, message } }

  // ── Per-domain expected records (provider none) ─────────────────────────
  expectedByDomain: {}, // { 'example.com': { records, edge_ip, hostnames, … } }
  expectedLoadingByDomain: {},
  expectedErrorByDomain: {},

  // ── Datacenters (cluster-api, cached in the service) ────────────────────
  datacenters: [], // [{ name, label, location }]
  datacentersLoading: false,
  // A 403 on GET /cluster/datacenters is remembered for the session so the
  // Domains page never asks again (the wizard uses the environments' set).
  datacentersDenied: false,

  // ── Last claim result (consumed by AddDomainModal verify step) ──────────
  claimResult: null,

  // ── Per-row busy map ────────────────────────────────────────────────────
  busy: {}
})

function makeBusyKey(scope, id, action) {
  return `${scope}:${id}:${action}`
}

function mapSet(map, key, value) {
  const next = { ...map }
  if (value === null || value === undefined || value === false) delete next[key]
  else next[key] = value
  return next
}

const getters = {
  /** Quick lookup by name. */
  domainByName: (s) => (name) =>
    s.domains.find((d) => d.domain === name) || null,

  /** True when the caller manages a personal domain (shared tier). */
  isPersonalScope: (s) => s.domainsScope === DOMAIN_SCOPE.PERSONAL,

  /** `{personal, limit, used, remaining, atLimit, canAdd}` — drives Add vs the plan line. */
  domainAllowance: (s) => domainAllowance({
    scope: s.domainsScope, limit: s.domainLimit, used: s.domainsUsed, domains: s.domains
  }),

  /** Convenience counters for the hero strip / tab badges. */
  verifiedCount: (s) =>
    s.domains.filter((d) => d.verification_status === 'verified').length,
  pendingCount: (s) =>
    s.domains.filter((d) => d.verification_status === 'pending').length,
  failedCount: (s) =>
    s.domains.filter((d) => d.verification_status === 'failed').length,
  selfManagedCount: (s) =>
    s.domains.filter((d) => d.public_provider === 'none').length,

  /** Total record-count summed across the records cache (sampled). */
  totalRecordsSampled: (s) => {
    return Object.values(s.recordsByDomain).reduce(
      (acc, list) => acc + (Array.isArray(list) ? list.length : 0),
      0
    )
  }
}

const mutations = {
  // ── Domains ──────────────────────────────────────────────────────────────
  SET_DOMAINS_LOADING(state, v) { state.domainsLoading = v },
  SET_DOMAINS_ERROR(state, v) { state.domainsError = v },
  SET_DOMAINS_ACCESS_DENIED(state, v) { state.domainsAccessDenied = !!v },
  SET_CANONICAL(state, v) { state.canonical = !!v },
  SET_DOMAINS_SCOPE(state, { scope, limit, used, reason, systemOrg, decided } = {}) {
    state.domainsScope = scope === DOMAIN_SCOPE.PERSONAL ? DOMAIN_SCOPE.PERSONAL : DOMAIN_SCOPE.ORGANIZATION
    state.domainsScopeAnswered = scope === DOMAIN_SCOPE.PERSONAL || scope === DOMAIN_SCOPE.ORGANIZATION ? scope : null
    state.domainsScopeDecided = decided !== false
    state.domainLimit = Number.isFinite(limit) ? limit : null
    state.domainsUsed = Number.isFinite(used) ? used : null
    state.domainAccessReason = typeof reason === 'string' ? reason : null
    state.domainsSystemOrg = systemOrg === true
  },
  /** A 409 domain_limit_reached: the allowance is full whatever the list said. */
  MARK_DOMAIN_LIMIT(state, { limit, planHint } = {}) {
    state.domainsScope = DOMAIN_SCOPE.PERSONAL
    state.domainsScopeAnswered = DOMAIN_SCOPE.PERSONAL
    if (Number.isFinite(limit)) state.domainLimit = limit
    if (state.domainLimit === null) state.domainLimit = 1
    state.domainsUsed = Math.max(state.domainsUsed || 0, state.domainLimit)
    state.domainPlanHint = planHint === undefined ? null : planHint
  },
  /** A release frees a personal slot. */
  RELEASE_DOMAIN_SLOT(state) {
    if (Number.isFinite(state.domainsUsed) && state.domainsUsed > 0) state.domainsUsed -= 1
  },
  SET_DOMAINS(state, list) {
    state.domains = Array.isArray(list) ? list : []
  },
  UPSERT_DOMAIN(state, domain) {
    if (!domain || !domain.domain) return
    const idx = state.domains.findIndex((d) => d.domain === domain.domain)
    if (idx === -1) {
      state.domains = [domain, ...state.domains]
    } else {
      const next = [...state.domains]
      next.splice(idx, 1, { ...next[idx], ...domain })
      state.domains = next
    }
  },
  REMOVE_DOMAIN(state, name) {
    state.domains = state.domains.filter((d) => d.domain !== name)
    state.recordsByDomain = mapSet(state.recordsByDomain, name, null)
    state.privateRecordsByDomain = mapSet(state.privateRecordsByDomain, name, null)
    state.publicLaneByDomain = mapSet(state.publicLaneByDomain, name, null)
    state.expectedByDomain = mapSet(state.expectedByDomain, name, null)
  },

  // ── Records ──────────────────────────────────────────────────────────────
  SET_RECORDS_LOADING(state, { domain, loading }) {
    state.recordsLoadingByDomain = mapSet(state.recordsLoadingByDomain, domain, loading ? true : null)
  },
  SET_RECORDS_ERROR(state, { domain, error }) {
    state.recordsErrorByDomain = mapSet(state.recordsErrorByDomain, domain, error || null)
  },
  SET_RECORDS(state, { domain, records }) {
    state.recordsByDomain = { ...state.recordsByDomain, [domain]: Array.isArray(records) ? records : [] }
  },
  SET_PUBLIC_LANE(state, { domain, lane }) {
    state.publicLaneByDomain = mapSet(state.publicLaneByDomain, domain, lane || null)
  },
  SET_PRIVATE_RECORDS(state, { domain, records }) {
    state.privateRecordsByDomain = { ...state.privateRecordsByDomain, [domain]: Array.isArray(records) ? records : [] }
  },

  // ── Expected records ─────────────────────────────────────────────────────
  SET_EXPECTED_LOADING(state, { domain, loading }) {
    state.expectedLoadingByDomain = mapSet(state.expectedLoadingByDomain, domain, loading ? true : null)
  },
  SET_EXPECTED_ERROR(state, { domain, error }) {
    state.expectedErrorByDomain = mapSet(state.expectedErrorByDomain, domain, error || null)
  },
  SET_EXPECTED(state, { domain, data }) {
    state.expectedByDomain = mapSet(state.expectedByDomain, domain, data || null)
  },

  // ── Datacenters ──────────────────────────────────────────────────────────
  SET_DATACENTERS_LOADING(state, v) { state.datacentersLoading = v },
  SET_DATACENTERS(state, list) { state.datacenters = Array.isArray(list) ? list : [] },
  SET_DATACENTERS_DENIED(state, v) { state.datacentersDenied = v === true },

  // ── Claim ────────────────────────────────────────────────────────────────
  SET_CLAIM_RESULT(state, v) { state.claimResult = v || null },
  CLEAR_CLAIM_RESULT(state) { state.claimResult = null },

  // ── Per-row busy ─────────────────────────────────────────────────────────
  SET_BUSY(state, { scope, id, action, busy }) {
    state.busy = mapSet(state.busy, makeBusyKey(scope, id, action), busy ? true : null)
  }
}

function toast(dispatch, type, message) {
  dispatch('showNotification', { type, message }, { root: true })
}

const actions = {
  // ── Domains ──────────────────────────────────────────────────────────────
  async loadDomains({ commit, rootState }) {
    commit('SET_DOMAINS_LOADING', true)
    commit('SET_DOMAINS_ERROR', null)
    commit('SET_DOMAINS_ACCESS_DENIED', false)
    const user = rootState?.auth?.user
    try {
      // Organisation callers are asked about `domain:viewer` first (a missing
      // grant never turns into a 403 request; a super-admin skips it). A
      // shared-tier caller asks dns-manager `/domains/access` instead — the
      // system org's grant is never theirs to hold. `null` answers fall
      // through to the list; the server is the control either way.
      const result = await loadDomainsScoped({
        probe: () => domainsApi.canViewDomains(),
        access: () => domainsApi.getDomainAccess(),
        listWithMeta: () => domainsApi.listMyDomainsWithMeta()
      }, {
        superAdmin: isSuperAdminUser(user),
        sharedTier: isSharedTierUser(user),
        systemOrg: isSystemOrgUser(user),
        isDenied: (e) => e instanceof DomainAccessDenied
      })
      commit('SET_DOMAINS_SCOPE', result)
      if (result.denied) throw new DomainAccessDenied()
      const list = result.domains
      commit('SET_DOMAINS', list)
      // The alias never carries `private_lane`; the canonical route always does.
      commit('SET_CANONICAL', !list.length || list.some((d) => d && d.private_lane !== undefined))
      return list
    } catch (error) {
      if (error instanceof DomainAccessDenied) {
        commit('SET_DOMAINS', [])
        commit('SET_DOMAINS_ACCESS_DENIED', true)
        return []
      }
      commit('SET_DOMAINS_ERROR', error?.message || 'Failed to load domains')
      throw error
    } finally {
      commit('SET_DOMAINS_LOADING', false)
    }
  },

  /** `payload` = `{ domain, datacenter?, public_provider?, subdomain_policy? }` (a bare string is accepted). */
  async claimDomain({ commit, dispatch }, payload) {
    const body = typeof payload === 'string' ? { domain: payload } : (payload || {})
    const domain = body.domain
    commit('SET_BUSY', { scope: 'domain', id: domain, action: 'claim', busy: true })
    try {
      const result = await domainsApi.claimDomain(body)
      commit('SET_CLAIM_RESULT', result)
      commit('UPSERT_DOMAIN', result)
      return result
    } catch (error) {
      if (error instanceof DomainLimitReached) {
        // The plan sentence renders inline in the wizard and replaces Add on the page.
        commit('MARK_DOMAIN_LIMIT', { limit: error.limit, planHint: error.planHint })
      } else if (error instanceof DomainConflict || error instanceof DomainPolicyError) {
        // The wizard renders these inline (contact-support / A-22 copy).
      } else {
        toast(dispatch, 'error', error?.message || 'Couldn’t add that domain.')
      }
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'domain', id: domain, action: 'claim', busy: false })
    }
  },

  async verifyDomain({ commit, dispatch }, domain) {
    commit('SET_BUSY', { scope: 'domain', id: domain, action: 'verify', busy: true })
    try {
      const result = await domainsApi.verifyDomain(domain)
      commit('UPSERT_DOMAIN', result)
      if (result?.verification_status === 'verified') {
        toast(dispatch, 'success', `“${domain}” verified — you can manage records now.`)
      } else {
        toast(dispatch, 'warn', 'TXT record not propagated yet — try again in a minute.')
      }
      return result
    } catch (error) {
      toast(dispatch, 'error', error?.message || 'Verification failed — check the TXT record and try again.')
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'domain', id: domain, action: 'verify', busy: false })
    }
  },

  async releaseDomain({ commit, dispatch }, domain) {
    commit('SET_BUSY', { scope: 'domain', id: domain, action: 'release', busy: true })
    try {
      await domainsApi.releaseDomain(domain)
      commit('REMOVE_DOMAIN', domain)
      commit('RELEASE_DOMAIN_SLOT')
      toast(dispatch, 'success', `“${domain}” released. Re-claim any time to manage records again.`)
    } catch (error) {
      toast(dispatch, 'error', error?.message || 'Couldn’t release that domain.')
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'domain', id: domain, action: 'release', busy: false })
    }
  },

  /** PATCH placement fields (datacenter / public_provider / …). A-22 refusals surface as DomainPolicyError. */
  async patchDomain({ commit, dispatch }, { domain, fields }) {
    commit('SET_BUSY', { scope: 'domain', id: domain, action: 'patch', busy: true })
    try {
      const updated = await domainsApi.patchDomain(domain, fields)
      if (updated && updated.domain) commit('UPSERT_DOMAIN', updated)
      toast(dispatch, 'success', `“${domain}” updated.`)
      return updated
    } catch (error) {
      toast(dispatch, error instanceof DomainPolicyError ? 'warn' : 'error', error?.message || 'Couldn’t update that domain.')
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'domain', id: domain, action: 'patch', busy: false })
    }
  },

  async checkDelegation({ commit, dispatch }, domain) {
    commit('SET_BUSY', { scope: 'domain', id: domain, action: 'delegation', busy: true })
    try {
      const data = await domainsApi.checkDelegation(domain)
      if (data && data.delegation) commit('UPSERT_DOMAIN', { domain, delegation: data.delegation })
      return data
    } catch (error) {
      toast(dispatch, 'error', error?.message || 'Couldn’t check the delegation.')
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'domain', id: domain, action: 'delegation', busy: false })
    }
  },

  async ensurePrivateLane({ commit, dispatch }, domain) {
    commit('SET_BUSY', { scope: 'domain', id: domain, action: 'lane', busy: true })
    try {
      const data = await domainsApi.ensurePrivateLane(domain)
      if (data && data.private_lane) commit('UPSERT_DOMAIN', { domain, private_lane: data.private_lane, datacenter: data.datacenter })
      const zs = data && data.private_lane && data.private_lane.zone_state
      toast(dispatch, zs === 'created' ? 'success' : 'warn', zs === 'created' ? `Internal zone for “${domain}” is in place.` : `Private lane: ${zs || 'unknown'}.`)
      return data
    } catch (error) {
      toast(dispatch, 'error', error?.message || 'Couldn’t create the internal zone.')
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'domain', id: domain, action: 'lane', busy: false })
    }
  },

  // ── Datacenters ──────────────────────────────────────────────────────────
  async loadDatacenters({ commit, state }, { force = false } = {}) {
    // A 403 this session is a settled answer — do not ask again.
    if (state.datacentersDenied && !force) return state.datacenters
    commit('SET_DATACENTERS_LOADING', true)
    try {
      const list = await domainsApi.listDatacenters({ force })
      commit('SET_DATACENTERS', list)
      return list
    } catch (error) {
      // A customer may not hold `datacenter` read; the wizard falls back to the
      // datacenters seen through the org's own environments.
      if (error?.response?.status === 403) commit('SET_DATACENTERS_DENIED', true)
      commit('SET_DATACENTERS', [])
      return []
    } finally {
      commit('SET_DATACENTERS_LOADING', false)
    }
  },

  // ── Records ──────────────────────────────────────────────────────────────
  async loadRecords({ commit }, domain) {
    commit('SET_RECORDS_LOADING', { domain, loading: true })
    commit('SET_RECORDS_ERROR', { domain, error: null })
    try {
      const { records, publicLane } = await domainsApi.listRecordsDetailed(domain)
      commit('SET_RECORDS', { domain, records })
      commit('SET_PUBLIC_LANE', { domain, lane: publicLane })
      return records
    } catch (error) {
      if (error instanceof DomainNotVerified) {
        commit('SET_RECORDS_ERROR', { domain, error: { kind: 'not_verified', message: error.message } })
      } else {
        commit('SET_RECORDS_ERROR', { domain, error: { kind: 'generic', message: error?.message || 'Failed to load records' } })
      }
      throw error
    } finally {
      commit('SET_RECORDS_LOADING', { domain, loading: false })
    }
  },

  async loadPrivateRecords({ commit }, domain) {
    try {
      const records = await domainsApi.listPrivateRecords(domain)
      commit('SET_PRIVATE_RECORDS', { domain, records })
      return records
    } catch (error) {
      // Non-fatal: the public lane is still useful. Console only — no toast.
      console.warn(`Failed to load private records for ${domain}:`, error?.message)
      commit('SET_PRIVATE_RECORDS', { domain, records: [] })
      return []
    }
  },

  /** GET expected-records for a provider-`none` domain (live public read-back). */
  async loadExpectedRecords({ commit }, { domain, hostnames = [], edgeIp = null, observed = true }) {
    commit('SET_EXPECTED_LOADING', { domain, loading: true })
    commit('SET_EXPECTED_ERROR', { domain, error: null })
    try {
      const data = await domainsApi.expectedRecords(domain, { hostnames, edgeIp, observed })
      commit('SET_EXPECTED', { domain, data })
      return data
    } catch (error) {
      commit('SET_EXPECTED_ERROR', { domain, error: error?.message || 'Couldn’t compute the expected records.' })
      throw error
    } finally {
      commit('SET_EXPECTED_LOADING', { domain, loading: false })
    }
  },

  /** Shared refusal handling for record writes. */
  _recordRefused({ commit, dispatch }, { error, domain, fallback }) {
    if (error instanceof PublicLaneSelfManaged) {
      // P-24: the platform never writes a self-managed public zone. Keep the
      // expected records the server sent so the Records tab shows them now.
      if (error.expectedRecords.length) {
        commit('SET_EXPECTED', { domain, data: { domain, records: error.expectedRecords, hostnames: [], edge_ip: null, public_provider: 'none', public_lane_self_managed: true } })
      }
      toast(dispatch, 'warn', error.message)
    } else if (error instanceof DomainNotVerified) {
      toast(dispatch, 'warn', error.message)
    } else {
      toast(dispatch, 'error', error?.message || fallback)
    }
  },

  /** Routes to the right service method based on the lane choice. */
  async createRecord({ commit, dispatch }, { domain, provider, payload }) {
    commit('SET_BUSY', { scope: 'record', id: domain, action: 'create', busy: true })
    try {
      let result
      if (provider === 'both') {
        result = await domainsApi.createDualRecord(domain, {
          name: payload.name,
          publicIp: payload.value, // dual records are A only
          privateIp: payload.privateIp || payload.value,
          ttl: payload.ttl
        })
      } else if (provider === 'pfsense') {
        result = await domainsApi.createPrivateRecord(domain, {
          name: payload.name,
          localIp: payload.value,
          ttl: payload.ttl,
          notes: payload.notes
        })
      } else {
        // Public lane — the provider resolves from the Domain on the server.
        result = await domainsApi.createRecord(domain, {
          provider: provider && provider !== 'public' ? provider : undefined,
          type: payload.type,
          name: payload.name,
          value: payload.value,
          ttl: payload.ttl
        })
      }
      toast(dispatch, 'success', `Record “${payload.name}” added.`)
      await dispatch('loadRecords', domain).catch(() => {})
      if (provider === 'pfsense' || provider === 'both') {
        await dispatch('loadPrivateRecords', domain).catch(() => {})
      }
      return result
    } catch (error) {
      dispatch('_recordRefused', { error, domain, fallback: 'Couldn’t add that record.' })
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'record', id: domain, action: 'create', busy: false })
    }
  },

  async updateRecord({ commit, dispatch }, { domain, recordId, payload }) {
    commit('SET_BUSY', { scope: 'record', id: domain, action: 'update', busy: true })
    try {
      const result = await domainsApi.updateRecord(domain, recordId, payload)
      toast(dispatch, 'success', 'Record updated.')
      await dispatch('loadRecords', domain).catch(() => {})
      return result
    } catch (error) {
      dispatch('_recordRefused', { error, domain, fallback: 'Couldn’t update that record.' })
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'record', id: domain, action: 'update', busy: false })
    }
  },

  async deleteRecord({ commit, dispatch }, { domain, recordId, provider }) {
    commit('SET_BUSY', { scope: 'record', id: domain, action: 'delete', busy: true })
    try {
      await domainsApi.deleteRecord(domain, recordId, provider)
      toast(dispatch, 'success', 'Record deleted.')
      await dispatch('loadRecords', domain).catch(() => {})
      if (provider === 'pfsense') await dispatch('loadPrivateRecords', domain).catch(() => {})
    } catch (error) {
      dispatch('_recordRefused', { error, domain, fallback: 'Couldn’t delete that record.' })
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'record', id: domain, action: 'delete', busy: false })
    }
  },

  async deleteDualRecord({ commit, dispatch }, { domain, name }) {
    commit('SET_BUSY', { scope: 'record', id: domain, action: 'delete-dual', busy: true })
    try {
      await domainsApi.deleteDualRecord(domain, name)
      toast(dispatch, 'success', `Dual record “${name}” deleted on both lanes.`)
      await dispatch('loadRecords', domain).catch(() => {})
      await dispatch('loadPrivateRecords', domain).catch(() => {})
    } catch (error) {
      dispatch('_recordRefused', { error, domain, fallback: 'Couldn’t delete that dual record.' })
      throw error
    } finally {
      commit('SET_BUSY', { scope: 'record', id: domain, action: 'delete-dual', busy: false })
    }
  },

  // ── Claim modal helpers ─────────────────────────────────────────────────
  clearClaimResult({ commit }) {
    commit('CLEAR_CLAIM_RESULT')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
